<template>
  <b-row>
    <b-col>
      <b-card
        no-body
        class="shipments-table"
      >
        <b-card-actions
          action-refresh
          @refresh="fetchData()"
        >
          <div class="overflow-x-scroll">
            <b-table
              :items="computedTableData"
              responsive
              :fields="fields"
              :sort-by.sync="sortBy"
              :sort-desc.sync="isSortDirDesc"
              stacked="sm"
              :busy="loading"
              class="mb-0"
              show-empty
            >
              <template #table-busy>
                <div class="text-center my-2">
                  <b-spinner class="align-middle mr-1" />
                  <strong>Loading...</strong>
                </div>
              </template>

              <template
                v-if="showLogoColumn"
                #cell(logo)="data"
              >
                <CompanyThumb
                  :logo="data.item.company.logo"
                  :name="data.item.company.name"
                />
              </template>

              <!-- Column: User -->
              <template #cell(users)="data">
                <b-media
                  v-if="data.item.users"
                  vertical-align="center"
                  class="avatar-list"
                >
                  <template #aside>
                    <b-avatar
                      v-for="(audit, index) in data.item.users"
                      :key="index"
                      v-b-tooltip.hover.top="audit.name"
                      size="32"
                      :src="data.item.avatar"
                      :text="getAvatar(audit.name)"
                      :to="{ name: 'user-edit', params: { userId: audit.id } }"
                    />
                  </template>
                </b-media>
              </template>

              <template #cell(document_number)="data">
                <div class="d-flex align-items-center">
                  <div>
                    <div class="font-weight-bolder">
                      <router-link :to="{ name: 'shipment', params: { documentNumber: data.item.document_number, shipmentId: data.item.id }}">
                        {{ data.item.document_number }}
                      </router-link>
                    </div>
                  </div>
                </div>
              </template>

              <template #cell(created_at)="data">
                {{ data.item.created_at | formatDate }}
              </template>

              <template #cell(status)="data">
                <div class="d-flex align-items-center">
                  <b-badge
                    v-if="getStatusInfo(data.item.status, 'variant')"
                    pill
                    :variant="getStatusInfo(data.item.status, 'variant')"
                    class="position-relative"
                  >
                    <template v-if="data.item.status === 'attention'">
                      <router-link :to="{ name: 'shipmentDeclarations', params: { shipmentId: data.item.id, tab: 'needs-attention' }}">
                        {{ getStatusInfo(data.item.status, 'label') }}
                        <b-badge
                          v-if="data.item.needs_attention_count > 0"
                          pill
                          variant="light"
                          class="text-warning needs-attention-badge"
                        >
                          {{ data.item.needs_attention_count }}
                        </b-badge>
                      </router-link>
                    </template>
                    <template v-else>
                      {{ getStatusInfo(data.item.status, 'label') }}
                      <b-badge
                        v-if="data.item.needs_attention_count > 0"
                        pill
                        variant="light"
                        class="text-warning needs-attention-badge"
                      >
                        {{ data.item.needs_attention_count }}
                      </b-badge>
                    </template>
                  </b-badge>
                </div>
              </template>

              <!-- Column: Actions -->
              <template #cell(actions)="data">
                <b-button-group>
                  <router-link :to="{ name: 'shipment', params: { documentNumber: data.item.document_number, shipmentId: data.item.id }}">
                    <b-button
                      variant="primary"
                      class="router-wrapped-action-button"
                      size="sm"
                    >
                      <feather-icon
                        icon="UploadIcon"
                        class="d-inline"
                      />
                    </b-button>
                  </router-link>
                  <b-button
                    v-if="canEdit"
                    variant="primary"
                    size="sm"
                    @click="edit(data.item.id)"
                  >
                    <feather-icon
                      icon="EditIcon"
                      class="d-inline"
                    />
                  </b-button>
                  <b-button
                    v-if="canDelete"
                    :disabled="data.item.declarations > 0"
                    variant="outline-danger"
                    size="sm"
                    @click="deleteShipment(data.item.id)"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      class="d-inline"
                    />
                  </b-button>
                </b-button-group>
              </template>

            </b-table>
          </div>
          <pagination :per-page="perPage" />
        </b-card-actions>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BCard, BTable, BBadge, BButton, BCol, BRow, BAvatar, BMedia, VBTooltip, BButtonGroup, BSpinner,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import handleError from '@/views/components/errorHandler'
import Pagination from '../../layouts/components/Pagination/Pagination.vue'
import avatarText from '@/views/components/avatar'
import CompanyThumb from '@/views/components/CompanyThumb.vue'
import { searchQuery } from '@core/utils/filter'
import { EventBus } from '@core/utils/utils'

export default {
  components: {
    BSpinner,
    CompanyThumb,
    BCard,
    BCardActions,
    BTable,
    BBadge,
    BCol,
    BRow,
    BButton,
    Pagination,
    BAvatar,
    BMedia,
    BButtonGroup,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: ['tableData', 'statuses', 'showLogoColumn'],
  data() {
    return {
      page: 1,
      perPage: 30,
      tableDataStore: [],
      sortBy: null,
      isSortDirDesc: null,
      filters: {},
      shipment: [],
      loading: false,
      fields: [
        ...[this.showLogoColumn ? { key: 'logo', label: 'Company', sortable: false, class: 'image-column' } : null],
        { key: 'document_number', label: 'Document number', sortable: false },
        { key: 'status', label: 'Status', sortable: false },
        {
          key: 'bags',
          label: 'Bags',
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
          sortable: false,
        },
        {
          key: 'goodsShipments',
          label: 'Parcels',
          thClass: 'text-right d-none d-sm-table-cell',
          tdClass: 'text-right d-none d-sm-table-cell',
          sortable: false,
        },
        {
          key: 'weight',
          label: 'Weight',
          thClass: 'text-right d-none d-sm-table-cell',
          tdClass: 'text-right d-none d-sm-table-cell',
          sortable: false,
        },
        {
          key: 'users',
          label: 'Users',
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
          sortable: false,
        },
        {
          key: 'created_at',
          label: 'Created',
          sortable: true,
          thClass: 'd-none d-sm-table-cell',
          tdClass: 'd-none d-sm-table-cell',
        },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    canDelete() {
      return this.$permissions().hasPermission('delete shipment')
    },
    canEdit() {
      return this.$permissions().hasPermission('edit shipment')
    },
    computedTableData() {
      return this.tableDataStore
    },
  },
  watch: {
    sortBy() {
      this.fetchData()
    },
    isSortDirDesc() {
      this.fetchData()
    },
  },
  mounted() {
    EventBus.$on('updateData', this.handleUpdateData)
    EventBus.$on('updateFilters', this.handleUpdateFilters)
    EventBus.$on('pageChange', this.handlePageChange)
    this.tableDataStore = this.$props.tableData
    this.fetchData(true)
  },
  beforeDestroy() {
    EventBus.$off('updateData', this.handleUpdateData)
    EventBus.$off('updateFilters', this.handleUpdateFilters)
    EventBus.$off('pageChange', this.handlePageChange)
  },
  methods: {
    handleUpdateData(data) {
      this.tableDataStore = Object.values(data)
    },
    handleUpdateFilters(data) {
      this.filters = data
      this.page = data?.page
      this.fetchData()
    },
    handlePageChange(newPage) {
      this.page = newPage
      this.fetchData()
    },
    getAvatar(name) {
      return avatarText(name)
    },
    edit(id) {
      this.$router.push({ name: 'edit-shipment', params: { shipmentId: id } })
    },
    deleteShipment(id) {
      this.$http.delete(`/v1/companies/${this.$activeCompany().data.company.id}/shipments/${id}`)
        .then(response => {
          this.fetchData()
          this.$toast({
            component: ToastificationContent,
            props: {
              title: response.data.message,
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    fetchData() {
      this.loading = true

      this.filters = {
        ...this.filters,
        sortBy: this.sortBy,
        sortDesc: this.isSortDirDesc,
        page: this.page,
        perPage: this.perPage,
      }

      // Request
      this.$http.get(searchQuery(`/v1/companies/${this.$activeCompany().data.company.id}/shipments\\`, this.filters))
        .then(response => {
          this.$data.shipments = response.data.data
          EventBus.$emit('updateData', response.data.data)
          EventBus.$emit('updateMeta', response.data.meta)
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getStatusInfo(statusCode, field) {
      const filteredStatus = this.statuses.find(status => status.value === statusCode)

      return filteredStatus ? filteredStatus[field] : null
    },
  },
}
</script>

<style scoped>
.router-wrapped-action-button {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  padding-bottom: .5rem;
}
</style>
